import { createSelector } from 'reselect';
import { memoize } from 'lodash';
import { actions as routerActions } from 'redux-router5';
import { navigateTo } from 'router/actions';
import { getActiveView, getDirtyLbView } from 'state/project-leaderboard/lb-views/selectors';
import {
  getCurrentRoute,
  getCurrentRouteParams,
  getQueryFromRouteParams,
} from 'state/selectors-global';
import type { AppState, NThunkDispatch } from 'state/types';
import type {
  GroupByStorage,
  GroupByStorageStrategy,
  LeaderboardListGroupKey,
} from '@neptune/shared/leaderboard-domain';
import { updateDirtyLbView } from './update-dirty-lb-view';
import { makeLbViewRouteState } from '@neptune/shared/lb-views-domain';
import { getRawGroupBy } from 'state/ui/route-params/selectors';
import { createGroupQuery } from 'common/query';
import { parseRouteParams } from 'router/router-utils';

const getInLbViewGroupRouteParamsSelector = createSelector(
  getCurrentRouteParams,
  getDirtyLbView,
  getActiveView,
  (routeParams, dirtyLbView, activeView) => {
    return memoize((key: LeaderboardListGroupKey[]) => {
      return makeLbViewRouteState(
        {
          query: createGroupQuery(dirtyLbView?.query, key),
          groupBy: undefined,
          groupByFieldType: undefined,
          groupByFieldAggregationMode: undefined,
        },
        routeParams,
        activeView,
      );
    });
  },
);

export const inLbViewGroupByStorageStrategy: GroupByStorageStrategy = {
  selector: (state: AppState) => getDirtyLbView(state)?.groupOptions,
  actionCreator: updateDirtyLbView,
  getGroupRouteParamsSelector: getInLbViewGroupRouteParamsSelector,
  currentQuerySelector: (state: AppState) => getDirtyLbView(state)?.query || '',
};

const getInUrlGroupRouteParamsSelector = createSelector(getCurrentRouteParams, (routeParams) => {
  return memoize((key: LeaderboardListGroupKey[]) => {
    return {
      ...routeParams,
      query: createGroupQuery(routeParams.query, key),
      groupBy: undefined,
      groupByFieldType: undefined,
      groupByFieldAggregationMode: undefined,
    };
  });
});

export const inUrlGroupByStorageStrategy: GroupByStorageStrategy = {
  selector: getRawGroupBy,
  actionCreator: updateUrl,
  getGroupRouteParamsSelector: getInUrlGroupRouteParamsSelector,
  currentQuerySelector: getQueryFromRouteParams,
};

function updateUrl(newParams: GroupByStorage) {
  return (
    dispatch: NThunkDispatch<ReturnType<typeof routerActions.navigateTo>>,
    getState: () => AppState,
  ) => {
    const route = getCurrentRoute(getState());
    dispatch(navigateTo(route.name, parseRouteParams({ ...route.params, ...newParams })));
  };
}
